import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Box, Typography, Divider, Skeleton } from '@mui/material'
import { format, parseISO } from 'date-fns'

import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import RequestAttachment from '../components/requestAttachment'
import RequestApproval from '../components/requestApproval'
import RequestButtonOptions from '../components/requestButtonOptions'
import leaveRequestStatusHandler from 'module/leave/util/leaveRequestStatusHandler'

import { useShowLeaveRequest, useDeleteLeaveRequest } from 'services/api/modules/leave/leaveRequest'
import CreditApproval from '../../leaveCreditDetails/components/creditApproval'

const LeaveRequestDetails = () => {
  const location = useLocation()
  let navigate = useNavigate()
  let { id } = useParams()
  const { data: item, isLoading } = useShowLeaveRequest(id)
  const mutation = useDeleteLeaveRequest()

  const [drawerState, setDrawerState] = useState({ right: true })
  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    if (location.state.from === 'dashboard') {
      navigate('/app/homepage/')
    } else {
      navigate(-1)
    }
  }

  const handleWithdrawRequest = () => {
    mutation.mutate(item.id)
  }

  return (
    <AdxDrawerComponent
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      headerprops={
        <Box
          sx={{
            display: 'flex',
            width: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {isLoading ? (
            <Box width="100%">
              <Skeleton width={'100%'} />
            </Box>
          ) : (
            <>
              <Typography variant="h4" color="dark-text.main" fontWeight={700}>
                {item?.duration === 1
                  ? `${item?.duration} day leave`
                  : `${item?.duration} days leave`}
              </Typography>
              {item?.status && <AdxStatusBar status={leaveRequestStatusHandler(item?.status)} />}
            </>
          )}
        </Box>
      }
      footerprops={
        <RequestButtonOptions
          requestStatus={item?.status}
          handleWithdraw={{
            fx: handleWithdrawRequest,
            isLoading: mutation.isLoading,
            disabled: mutation.isLoading,
          }}
          updatedAt={item?.updated_at}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'flex-start', lg: 'center' },
            }}
          >
            <Typography
              variant="body1"
              color="light-gray-text.main"
              sx={{ width: { xs: '100%', lg: '25%' } }}
              fontWeight={700}
            >
              Type
            </Typography>

            {isLoading ? (
              <Box width={'100%'}>
                <Skeleton width={'100%'} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '80%',
                  columnGap: 2,
                }}
              >
                <Typography variant="body1" color="dark-text.main">
                  {item?.leave_policy_name}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'flex-start', lg: 'center' },
            }}
          >
            <Typography
              variant="body1"
              color="light-gray-text.main"
              sx={{ width: { xs: '100%', lg: '25%' } }}
              fontWeight={700}
            >
              Start Date
            </Typography>
            {isLoading ? (
              <Box width={'100%'}>
                <Skeleton />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '80%',
                  columnGap: 2,
                }}
              >
                <Typography variant="body1" color="dark-text.main">
                  {item?.start_date && format(parseISO(item?.start_date), 'dd/MM/yyyy')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'flex-start', lg: 'center' },
            }}
          >
            <Typography
              variant="body1"
              color="light-gray-text.main"
              sx={{ width: { xs: '100%', lg: '25%' } }}
              fontWeight={700}
            >
              End Date
            </Typography>
            {isLoading ? (
              <Box width={'100%'}>
                <Skeleton />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '80%',
                  columnGap: 2,
                }}
              >
                <Typography variant="body1" color="dark-text.main">
                  {item?.end_date && format(parseISO(item?.end_date), 'dd/MM/yyyy')}
                </Typography>
              </Box>
            )}
          </Box>
          {item?.reason ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'flex-start', lg: 'center' },
              }}
            >
              <Typography
                variant="body1"
                color="light-gray-text.main"
                sx={{ width: '25%' }}
                fontWeight={700}
              >
                Reason
              </Typography>
              {isLoading ? (
                <Box width={'100%'}>
                  <Skeleton height={50} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '80%',
                    columnGap: 2,
                  }}
                >
                  <Typography variant="body1" color="dark-text.main">
                    {item?.reason}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}
          {item?.attachment ? (
            <>
              {isLoading ? (
                <Box width={'100%'}>
                  <Skeleton height={100} />
                </Box>
              ) : (
                <Box>
                  <RequestAttachment item={item} />
                </Box>
              )}
            </>
          ) : null}
          {isLoading ? (
            <Box width={'100%'}>
              <Skeleton height={100} />
            </Box>
          ) : (
            <Box>
              {item?.leave_policy_id === 5 ? (
                <CreditApproval approvalArr={item?.leave_approval} requestItem={item} />
              ) : (
                <RequestApproval approvalArr={item?.leave_approval} requestItem={item} />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </AdxDrawerComponent>
  )
}

export default LeaveRequestDetails
